import React from 'react';
import { Accordion } from '@noths/polaris-client-ribbons-design-system';
import { priceFormatter } from '@noths/polaris-client-utils';
import type { Price } from '@noths/polaris-dev-ts-types';
import { CurrencySymbol } from '@noths/polaris-dev-ts-types';

import {
  ADDED_COPY,
  ITEM_PRICE_COPY,
  OPTIONS_COPY,
  PERSONALISATION_COPY,
  REMOVED_COPY,
  TOTAL_COPY,
} from 'src/components/molecules/PriceBreakdown/constants/copy';
import {
  PRICE_BREAKDOWN_ADDED_TESTID,
  PRICE_BREAKDOWN_REMOVED_TESTID,
  PRICE_BREAKDOWN_TESTID,
} from 'src/components/molecules/PriceBreakdown/constants/testIds';
import { getFormattedPersonalisationAmount } from 'src/utils/price';
import * as styles from './PriceBreakdown.styles';

export interface PersonalisationBreakdown {
  amountAdded: number;
  amountRemoved: number;
}

export interface PriceBreakdownStateProps {
  optionsTotal: number;
  personalisable: boolean;
  priceBreakdown: PersonalisationBreakdown;
  productPrice: Price;
  totalPrice: number;
}

export interface PriceBreakdownOwnProps {
  expandable?: boolean;
  totalDivider?: boolean;
}

type PriceBreakdownProps = PriceBreakdownOwnProps & PriceBreakdownStateProps;

const AddedAndRemovedAmounts = ({
  priceBreakdown,
  priceDigitLength,
  productPrice,
}: Pick<PriceBreakdownProps, 'priceBreakdown' | 'productPrice'> & {
  priceDigitLength: number;
}) => (
  <>
    {priceBreakdown.amountAdded > 0 && (
      <>
        <p css={styles.priceLabel}>{ADDED_COPY}</p>
        <p css={styles.priceAmount} data-testid={PRICE_BREAKDOWN_ADDED_TESTID}>
          <span css={styles.priceAmountPlusSymbol}>+</span>
          {priceFormatter(productPrice.currency, priceBreakdown.amountAdded)}
        </p>
      </>
    )}
    {priceBreakdown.amountRemoved < 0 && (
      <>
        <p css={styles.priceLabel}>{REMOVED_COPY}</p>{' '}
        <p css={styles.priceAmount} data-testid={PRICE_BREAKDOWN_REMOVED_TESTID}>
          <span css={styles.priceAmountMinusSymbol} data-is-multi-digit={priceDigitLength > 1}>
            &minus;
          </span>
          {priceFormatter(productPrice.currency, priceBreakdown.amountRemoved).replace('-', '')}
        </p>
      </>
    )}
  </>
);

export const PriceBreakdown = ({
  expandable,
  optionsTotal,
  personalisable,
  priceBreakdown,
  productPrice,
  totalDivider,
  totalPrice,
}: PriceBreakdownProps) => {
  const headingText = personalisable ? PERSONALISATION_COPY : OPTIONS_COPY;
  const { value: formattedPersonalisationsTotalAmount } = getFormattedPersonalisationAmount(
    productPrice.currency,
    optionsTotal,
  );

  const priceDigitLength = formattedPersonalisationsTotalAmount
    .replace(CurrencySymbol[productPrice.currency], '')
    .replace('-', '')
    .split('.')[0].length;

  const showTotalDivider = totalDivider && optionsTotal !== 0;

  return (
    <div css={styles.priceBreakdown} data-testid={PRICE_BREAKDOWN_TESTID}>
      {optionsTotal !== 0 && (
        <>
          <p css={styles.priceLabel}>{ITEM_PRICE_COPY}</p>
          <p css={styles.priceAmount}>
            {priceFormatter(productPrice.currency, productPrice.amount)}
          </p>

          {expandable ? (
            <div css={styles.accordionStyles}>
              <Accordion
                id="personalisation-breakdown-accordion"
                title={
                  <div css={styles.accordionTitle}>
                    <span>{headingText}</span>
                    <span css={styles.accordionTotalAmount}>
                      {optionsTotal < 0 && <>&minus; </>}
                      {priceFormatter(productPrice.currency, optionsTotal).replace('-', '')}
                    </span>
                  </div>
                }
              >
                <div css={styles.expandablePersonalisationBreakdown}>
                  <AddedAndRemovedAmounts
                    priceBreakdown={priceBreakdown}
                    priceDigitLength={priceDigitLength}
                    productPrice={productPrice}
                  />
                </div>
              </Accordion>
            </div>
          ) : (
            <div css={styles.personalisationBreakdown}>
              <p css={styles.personalisationBreakdownCopy}>{headingText}</p>
              <AddedAndRemovedAmounts
                priceBreakdown={priceBreakdown}
                priceDigitLength={priceDigitLength}
                productPrice={productPrice}
              />
            </div>
          )}
        </>
      )}

      <p css={[styles.totalLabel, showTotalDivider && styles.totalDivider]}>{TOTAL_COPY}</p>
      <p css={[styles.totalPrice, showTotalDivider && styles.totalDivider]}>
        {priceFormatter(productPrice.currency, totalPrice)}
      </p>
    </div>
  );
};
